import React from "react";
import { motion } from "framer-motion";

/**
 * Map zoomed in to Asia + Middle East region.
 * viewBox tightly framed around our trade corridors.
 * Origin coordinates are kept on a 1000x500 source grid; viewBox crops to the relevant window.
 */
const points = {
  Singapore: { x: 745, y: 295, type: "origin" },
  Vietnam: { x: 745, y: 245, type: "origin" },
  Indonesia: { x: 780, y: 320, type: "origin" },
  Malaysia: { x: 735, y: 285, type: "origin" },
  Thailand: { x: 715, y: 240, type: "origin" },
  Philippines: { x: 820, y: 255, type: "origin" },
  India: { x: 625, y: 235, type: "destination" },
  UAE: { x: 565, y: 230, type: "destination" },
  "Saudi Arabia": { x: 540, y: 225, type: "destination" },
  Oman: { x: 583, y: 245, type: "destination" },
  Qatar: { x: 558, y: 222, type: "destination" },
};

const buildPath = (from, to) => {
  const mx = (from.x + to.x) / 2;
  const my = Math.min(from.y, to.y) - 35;
  return `M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`;
};

const routes = [
  ["Singapore", "India"],
  ["Singapore", "UAE"],
  ["Vietnam", "Saudi Arabia"],
  ["Indonesia", "Qatar"],
  ["Malaysia", "Oman"],
  ["Thailand", "UAE"],
  ["Philippines", "Saudi Arabia"],
  ["Indonesia", "India"],
];

export const TradeFlow = () => {
  return (
    <section
      id="trade-flow"
      className="relative bg-white py-28 lg:py-36"
      data-testid="trade-flow-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-12 lg:mb-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-vynex-gold" />
              <span className="eyebrow text-vynex-emerald">Trade Flow</span>
            </div>
            <h2 className="font-display text-4xl lg:text-[56px] leading-[1.05] tracking-tight text-vynex-navy">
              Movement, connectivity, and{" "}
              <span className="italic text-vynex-emerald">global reach.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 text-slate-600 leading-relaxed self-end">
            We channel cargo from established South East Asian supply origins
            into demand centres across India and the Gulf — efficiently and on
            schedule.
          </div>
        </div>

        {/* Large map - zoomed to Asia + Middle East */}
        <div className="relative bg-vynex-light border border-vynex-silver p-3 sm:p-6 lg:p-8">
          <svg
            viewBox="495 170 360 195"
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-auto block min-h-[420px] sm:min-h-[520px] lg:min-h-[640px]"
            xmlns="http://www.w3.org/2000/svg"
            data-testid="trade-flow-map"
          >
            <defs>
              <linearGradient id="flow" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#087a55" />
                <stop offset="100%" stopColor="#c99a3a" />
              </linearGradient>
            </defs>

            {/* Stylized land masses for Asia + Middle East region only */}
            <g fill="#061b33" opacity="0.18">
              {Array.from({ length: 50 }).flatMap((_, i) =>
                Array.from({ length: 25 }).map((__, j) => {
                  const x = i * 20 + 10;
                  const y = j * 20 + 10;
                  // Only land cells within the focused viewBox region
                  const inViewWindow =
                    x >= 495 && x <= 855 && y >= 170 && y <= 365;
                  if (!inViewWindow) return null;
                  const inLand =
                    // Arabian Peninsula
                    (x > 520 && x < 600 && y > 200 && y < 270) ||
                    // Iran / Caspian
                    (x > 580 && x < 640 && y > 180 && y < 230) ||
                    // India subcontinent
                    (x > 600 && x < 680 && y > 200 && y < 290) ||
                    // Indochina / Thailand / Vietnam / Cambodia / Myanmar
                    (x > 680 && x < 760 && y > 200 && y < 290) ||
                    // Malaysia peninsula
                    (x > 720 && x < 760 && y > 280 && y < 310) ||
                    // Indonesia / Sumatra / Java / Borneo
                    (x > 720 && x < 820 && y > 295 && y < 360) ||
                    // Philippines
                    (x > 800 && x < 840 && y > 235 && y < 290) ||
                    // South China coast
                    (x > 760 && x < 820 && y > 190 && y < 240) ||
                    // East Africa edge (Horn of Africa)
                    (x > 495 && x < 540 && y > 240 && y < 290);
                  return inLand ? (
                    <circle key={`${i}-${j}`} cx={x} cy={y} r="1.3" />
                  ) : null;
                })
              )}
            </g>

            {/* Trade flow lines */}
            {routes.map(([from, to], idx) => {
              const f = points[from];
              const t = points[to];
              const d = buildPath(f, t);
              return (
                <g key={`${from}-${to}-${idx}`}>
                  <path
                    d={d}
                    fill="none"
                    stroke="#061b33"
                    strokeOpacity="0.18"
                    strokeWidth="0.9"
                  />
                  <motion.path
                    d={d}
                    fill="none"
                    stroke="url(#flow)"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 0.9 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 1.8,
                      delay: idx * 0.15,
                      ease: "easeInOut",
                    }}
                  />
                </g>
              );
            })}

            {/* Nodes with bigger labels (scale appropriate for cropped view) */}
            {Object.entries(points).map(([name, p]) => {
              const isOrigin = p.type === "origin";
              const color = isOrigin ? "#087a55" : "#c99a3a";
              // Adjust label position for crowded Middle East cluster
              const labelOffset = name === "Qatar" ? { dx: -2, dy: -8 }
                : name === "UAE" ? { dx: 6, dy: -2 }
                : name === "Saudi Arabia" ? { dx: -38, dy: -2 }
                : name === "Oman" ? { dx: 5, dy: 10 }
                : { dx: 5, dy: -4 };
              return (
                <g key={name}>
                  <circle cx={p.x} cy={p.y} r="2.6" fill={color} />
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r="5"
                    fill="none"
                    stroke={color}
                    strokeOpacity="0.5"
                    strokeWidth="0.8"
                  />
                  <text
                    x={p.x + labelOffset.dx}
                    y={p.y + labelOffset.dy}
                    fontSize="5.5"
                    fontFamily="IBM Plex Sans, sans-serif"
                    fill="#061b33"
                    fontWeight="600"
                  >
                    {name}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 flex flex-col gap-2 bg-white p-3 sm:p-4 border border-vynex-silver">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-vynex-emerald" />
              <span className="text-xs text-vynex-navy tracking-wide">
                Import Sources
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-vynex-gold" />
              <span className="text-xs text-vynex-navy tracking-wide">
                Export Markets
              </span>
            </div>
          </div>

          {/* Region label */}
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 eyebrow text-vynex-navy/50 bg-white/80 px-3 py-1.5 border border-vynex-silver">
            Asia · Middle East
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px mt-12 bg-vynex-silver border border-vynex-silver">
          <div className="bg-white p-8 lg:p-10">
            <div className="eyebrow text-vynex-emerald mb-5">
              Import Sources
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {[
                "Singapore",
                "Vietnam",
                "Indonesia",
                "Malaysia",
                "Thailand",
                "Philippines",
              ].map((c) => (
                <span
                  key={c}
                  className="font-display text-xl lg:text-2xl text-vynex-navy"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
          <div className="bg-vynex-navy text-white p-8 lg:p-10">
            <div className="eyebrow text-vynex-gold mb-5">Export Markets</div>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {["India", "UAE", "Saudi Arabia", "Oman", "Qatar"].map((c) => (
                <span key={c} className="font-display text-xl lg:text-2xl">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TradeFlow;
